'use client';

import breakpoints from '@carvana/merch-tailwind/breakpoints';
import { appAssetsCdnUrl } from '~/constants/environment';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import optimizeImage, { type Options } from '~/utilities/optimizeImage';
import toLowerKebabCase from '~/utilities/toLowerKebabCase';
import GeolocationButton from './GeolocationButton';
import {
  GoldText,
  Heading,
  HeroWrapper,
  Subtitle,
  TextContent
} from './Hero.styles';

type OwnProps = {
  make: string;
  parentModel: string;
};

function ParentModelHero({ make, parentModel }: OwnProps) {
  const heroBackgroundMobileSrc = `${appAssetsCdnUrl}/images/new-car/hero-parent-model-background-small-new.svg`;
  const heroBackgroundDesktopSrc = `${appAssetsCdnUrl}/images/new-car/hero-parent-model-background-large.svg`;

  const vehicleBasePath = `${appAssetsCdnUrl}/images/new-car/${toLowerKebabCase(
    make
  )}/${toLowerKebabCase(parentModel)}`;
  const imageOptions: Options = { format: 'auto' as const, quality: 90 };
  const vehicleDesktopSrc = optimizeImage(`${vehicleBasePath}-desktop.png`, {
    ...imageOptions,
    height: 300
  });
  const vehicleMobileSrc = optimizeImage(`${vehicleBasePath}-mobile.png`, {
    ...imageOptions
  });

  return (
    <HeroWrapper data-testid="hero" className="h-[198px]">
      <picture aria-hidden="true">
        <source
          media={`(min-width: ${breakpoints.md}px)`}
          srcSet={heroBackgroundDesktopSrc}
        />
        <img
          alt=""
          className="absolute inset-0 size-full object-cover object-[30%_80%]"
          src={heroBackgroundMobileSrc}
        />
      </picture>
      <TextContent>
        <Heading className="md:t-heading-2xl flex flex-col gap-xs">
          <span>Find your perfect</span>
          <GoldText className="t-heading-xl md:t-heading-2xl">
            {make} {parentModel}
          </GoldText>
        </Heading>
        <Subtitle className="max-w-[75%] md:max-w-[45%]">
          All brand new models — pickup or delivery as soon as today!
        </Subtitle>
        <GeolocationButton />
      </TextContent>
      <picture className="absolute bottom-0 right-0 z-10 w-[178px] sm:w-[300px] md:top-1/2 md:bottom-auto md:w-[477px] md:-translate-y-1/2 md:-right-44 lg:right-0">
        <source
          media={`(min-width: ${breakpoints.md}px)`}
          srcSet={vehicleDesktopSrc}
        />
        <img
          alt={`${make} ${parentModel}`}
          className="block w-full"
          src={vehicleMobileSrc}
        />
      </picture>
    </HeroWrapper>
  );
}

export default withErrorBoundary<OwnProps>(ParentModelHero, {
  section: 'hero'
});
